import {Navigate, Route, Routes} from 'react-router-dom';
import {Toaster} from 'sonner';

import {IS_DEV_ENV, IS_PROD_ENV} from 'config';
import {useInitializeAmplitude} from 'core/App/hooks/useInitializeAmplitude';
import {useInitializeIntercom} from 'core/App/hooks/useInitializeIntercom';
import {
  COMPARE,
  ENNABL_CHAT_AI,
  ENNABL_COMMAND,
  ENNABL_DECISIONS,
  ENNABL_PRIVACY_POLICY,
  ENNABL_DOCUMENTS,
  ENNABL_GROWTH,
  ENNABL_IMPERSONATE,
  ENNABL_LOSS_RUNS,
  ENNABL_MGA_DASHBOARD,
  ENNABL_SETTINGS,
  ENNABL_WORKFLOWS,
  LIBRARY,
} from 'core/constants/paths';
import {useFeatureFlag, useIsInternalUser, useRestrictedAccessPermissions} from 'core/hooks/authHooks';
import {useDefaultRoute} from 'core/hooks/useDefaultRoute';
import {useEntityAccess} from 'core/hooks/useEntityAccess';
import {impersonationToken} from 'core/impersonation';
import {EnnablAIPromoDialog} from 'features/EnnablAIPromoDialog';
import {GlobalFilterProvider} from 'features/GlobalFilterProvider';
import {NotificationDialog} from 'features/NotificationDialog';
import {useDetectNewFeatures} from 'modules/decisionsNext/sections/WhatsNewSection';
import {useWhatsNewToast} from 'modules/decisionsNext/sections/WhatsNewSection/hooks/useWhatsNewToast';
import {ConfirmationManager} from 'utils/confirmation';
import {lazyLoad} from 'utils/lazyLoadUtils';
import {FeatureFlags} from 'core/constants/featureFlags';

import {DownloadMobileAppDialog} from 'components/DownloadMobileAppDialog';
import {Analytics} from 'components/Head/Analytics';
import {LoadingScreen} from 'components/LoadingScreen';
import {OfflineBanner} from 'components/OfflineBanner';

import {useInitializeLogRocket} from './hooks/useInitializeLogRocket';
import {useInitializePostHog} from './hooks/useInitializePostHog';
import {useInitializeWebViewApp} from './hooks/useInitializeWebViewApp';

const lazyOpts = {fallback: <LoadingScreen />};

const CommandRedirect = lazyLoad(() => import(/* command-redirect */ 'core/components/CommandRedirect'), lazyOpts);

const MgaFilterOverrideProvider = lazyLoad(
  () =>
    import(
      /* webpackChunkName:'mga-filter-override-provider' */ 'features/GlobalFilterProvider/overrides/MgaFilterOverrideProvider'
    ),
  lazyOpts
);
const DevRoot = IS_DEV_ENV
  ? lazyLoad(() => import(/* webpackChunkName:'dev-root' */ 'modules/dev/DevRoot'), lazyOpts)
  : () => null;
const DecisionsRoot = lazyLoad(
  () => import(/* webpackChunkName:'decisions-root' */ 'modules/decisionsNext/components/Root'),
  lazyOpts
);
const DashboardRoot = lazyLoad(
  () => import(/* webpackChunkName:'mga-dashboard-root' */ 'modules/dashboard/components/Root'),
  lazyOpts
);
const GrowthRoot = lazyLoad(
  () => import(/* webpackChunkName:'growth-root' */ 'modules/growth/components/Root'),
  lazyOpts
);
const AIChatRoot = lazyLoad(
  () => import(/* webpackChunkName:'chat-ai-root' */ 'modules/aiChat/components/Root'),
  lazyOpts
);
const LossRunsRoot = lazyLoad(
  () => import(/* webpackChunkName:'loss-runs-root' */ 'modules/lossRuns/LossRunsRoot'),
  lazyOpts
);
const WorkflowsRoot = lazyLoad(
  () => import(/* webpackChunkName:'workflows-root' */ 'modules/workflows/WorkflowsRoot'),
  lazyOpts
);
const DocumentsLibraryRoot = lazyLoad(
  () => import(/* webpackChunkName:'documents-library-root' */ 'modules/documentsLibrary/DocumentsLibraryRoot'),
  lazyOpts
);
const DocumentsCompareRoot = lazyLoad(
  () => import(/* webpackChunkName:'documents-compare-root' */ 'modules/documentsCompare/DocumentsCompareRoot'),
  lazyOpts
);
const NotFoundPage = lazyLoad(
  () => import(/* webpackChunkName:'not-found-page' */ 'components/errors/NotFoundPage'),
  lazyOpts
);
const ImpersonationBanner = lazyLoad(
  () => import(/* webpackChunkName:'impersonation-banner' */ 'components/ImpersonationBanner'),
  {
    fallback: null,
  }
);
const ImpersonateRoot = lazyLoad(
  () => import(/* webpackChunkName:'impersonate-root' */ 'modules/impersonate/ImpersonateRoot'),
  lazyOpts
);
const ModifierRoot = lazyLoad(
  () => import(/* webpackChunkName:'modifier-root' */ 'modules/modifier/ModifierRoot'),
  lazyOpts
);

const PrivacyPolicyPage = lazyLoad(
  () => import(/* webpackChunkName:'privacy-policy-page' */ 'pages/PrivacyPolicyPage'),
  lazyOpts
);

const UserAgreementGate = lazyLoad(() => import('core/components/UserAgreementGate'), lazyOpts);

const SettingsRoot = lazyLoad(
  () => import(/* webpackChunkName:'settings-root' */ 'modules/settings/SettingsRoot'),
  lazyOpts
);

const App = () => {
  const defaultRoute = useDefaultRoute();
  const isInternalUser = useIsInternalUser();
  const withPrivacyPolicy = useFeatureFlag(FeatureFlags.PrivacyPolicyPage);

  const {
    withMga,
    withEnnablAI,
    withDocumentsCompare,
    withWorkflows,
    withLossRuns,
    withDecisions,
    withWhatsNew,
    withDocumentsLibrary,
    withGrowth,
    withCommand,
  } = useEntityAccess();

  const {restrictedAccess, onlyReportStudioPermission} = useRestrictedAccessPermissions();

  useInitializeIntercom();
  useInitializeWebViewApp();
  useInitializeLogRocket();
  useInitializeAmplitude();
  useInitializePostHog();

  useDetectNewFeatures(withWhatsNew);
  useWhatsNewToast(withWhatsNew);

  return (
    <GlobalFilterProvider>
      {IS_PROD_ENV && <Analytics />}
      {impersonationToken && <ImpersonationBanner />}
      <ConfirmationManager />
      <DownloadMobileAppDialog />
      <NotificationDialog />
      <EnnablAIPromoDialog />

      <UserAgreementGate>
        <Routes>
          <Route index element={<Navigate replace to={defaultRoute} />} />

          {withMga && (
            <Route
              path={`${ENNABL_MGA_DASHBOARD}/*`}
              element={
                <MgaFilterOverrideProvider>
                  <DashboardRoot />
                </MgaFilterOverrideProvider>
              }
            />
          )}

          {withCommand && <Route path={`${ENNABL_COMMAND}/*`} element={<CommandRedirect />} />}
          {withDecisions && <Route path={`${ENNABL_DECISIONS}/*`} element={<DecisionsRoot />} />}
          {withGrowth && <Route path={`${ENNABL_GROWTH}/*`} element={<GrowthRoot />} />}
          {withEnnablAI && <Route path={`${ENNABL_CHAT_AI}/*`} element={<AIChatRoot />} />}
          {withLossRuns && <Route path={`${ENNABL_LOSS_RUNS}/*`} element={<LossRunsRoot />} />}
          {withPrivacyPolicy && <Route path={ENNABL_PRIVACY_POLICY} element={<PrivacyPolicyPage />} />}
          {withWorkflows && <Route path={`${ENNABL_WORKFLOWS}/*`} element={<WorkflowsRoot />} />}

          {withWorkflows && (
            <Route path={`${ENNABL_DOCUMENTS}/*`}>
              <Route index element={<Navigate replace to={ENNABL_WORKFLOWS} />} />

              {withDocumentsLibrary && <Route path={`${LIBRARY}/*`} element={<DocumentsLibraryRoot />} />}
              {withDocumentsCompare && <Route path={`${COMPARE}/*`} element={<DocumentsCompareRoot />} />}

              <Route
                path='*'
                element={
                  restrictedAccess ? <Navigate replace to={defaultRoute} /> : <NotFoundPage backLink={defaultRoute} />
                }
              />
            </Route>
          )}

          <Route path={`${ENNABL_SETTINGS}/*`} element={<SettingsRoot />} />
          {IS_DEV_ENV && <Route path='/dev/*' element={<DevRoot />} />}

          {isInternalUser && (
            <>
              <Route path={ENNABL_IMPERSONATE} element={<ImpersonateRoot />} />
              <Route path='/modifier' element={<ModifierRoot />} />
            </>
          )}

          <Route
            path='*'
            element={
              onlyReportStudioPermission ? (
                <Navigate replace to={defaultRoute} />
              ) : (
                <NotFoundPage backLink={defaultRoute} />
              )
            }
          />
        </Routes>
      </UserAgreementGate>

      <Toaster expand closeButton visibleToasts={5} />

      <OfflineBanner />
    </GlobalFilterProvider>
  );
};

export default App;
